import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { FIRST_PAGE, MAX_PER_PAGE } from 'constants/constants';
import { QueryTasksArgs, TaskPage } from 'gql/graphql';
import { gql } from 'graphql-request';
import { TGenericError } from 'types/TGenericError';
import { buildQueryKey } from 'utils/buildQueryKey';
import clientUtil from 'utils/clientUtil';

type TVariables = QueryTasksArgs;
type TData = TaskPage;

const MOCK: TVariables = {
  comments: undefined,
  page: FIRST_PAGE,
  perPage: MAX_PER_PAGE,
  skipPagination: undefined,
  sortAttribute: undefined,
  sortOrder: undefined,
  sourceLanguageCode: undefined,
  status: undefined,
  targetLanguageCode: undefined,
  taskId: undefined,
  translationRequestVersion: undefined
};

export const useTasksInProgress = (
  variables: TVariables = MOCK,
  options: Omit<UseQueryOptions<unknown, TGenericError, TData>, 'queryKey'> = {}
) => {
  const client = clientUtil();

  const graphql = gql`
    query tasks(
      $comments: CommentsFilterEnum
      $sourceLanguageCode: String
      $targetLanguageCode: String
      $status: [TaskQueryStatusEnum!]
      $translationRequestVersion: ID
      $taskId: ID
      $page: Int
      $perPage: Int
    ) {
      tasks(
        comments: $comments
        sourceLanguageCode: $sourceLanguageCode
        targetLanguageCode: $targetLanguageCode
        status: $status
        translationRequestVersion: $translationRequestVersion
        taskId: $taskId
        page: $page
        perPage: $perPage
      ) {
        nodes {
          id
          sourceLanguageCode
          targetLanguageCode
          totalTranslatableWordCount
          orderQuality
          status
          waitingInReviewSince
          comments {
            id
            read
            writtenByClient
          }
          precedentTrs {
            status
            version
          }
          translatorAccount {
            ... on TranslatorAccount {
              user {
                name
              }
            }
          }
          proofreaderAccount {
            user {
              name
            }
          }
        }
        hasNextPage
        hasPreviousPage
        nodesCount
        pagesCount
      }
    }
  `;

  return useQuery<unknown, TGenericError, TData>({
    ...options,
    queryKey: ['useTasksInProgress', ...buildQueryKey({ ...MOCK, ...variables })],
    queryFn: async () =>
      client.request({
        data: {
          gql: graphql,
          variables
        }
      })
  });
};
